import {
  useCallback,
  useRef,
  useState,
} from "react"

import { auth } from "@/api/firebase"

export function useAuthedRpc<TArgs extends any[], TResult>(
  executor: (user: { uid: string }, ...args: TArgs) => Promise<TResult>
) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Store executor in ref so it doesn't cause re-renders
  // This is a standard React pattern for stable callbacks
  const executorRef = useRef(executor)
  executorRef.current = executor

  const run = useCallback(
    async (...args: TArgs): Promise<TResult> => {
      setLoading(true)
      setError(null)

      const user = auth.currentUser
      if (!user) {
        const err = new Error("User not authenticated.")
        setError(err.message)
        setLoading(false)
        throw err
      }

      try {
        const result = await executorRef.current(user, ...args)
        return result
      } catch (err: any) {
        console.error("[useAuthedRpc] error", err)
        setError(err?.message || "Unknown error")
        throw err
      } finally {
        setLoading(false)
      }
    },
    [] // Now stable - no dependencies needed
  )

  return { run, loading, error }
}
